import { FC, useMemo, useCallback, useEffect } from 'react'
import { FloatingPane } from 'interfaces/floatingPanes'
import { createFloatingPaneByTypeSelector } from 'selectors/floatingPanesSelectors'
import { useStoreSelector, useStoreDispatch } from 'helpers/reactReduxHelpers'
import { FloatingPaneLayout } from 'components/features/pfm/FloatingPaneLayout/FloatingPaneLayout'
import HouseEditForm from 'components/features/pfm/HouseEditForm/HouseEditForm'
import { homeToFormValues } from 'helpers/propertyHelpers'
import { HOME_FORM_INITIAL_VALUES } from 'constants/forms'
import { getItemFromCollection } from 'helpers/normalizeHelpers'
import { onFormSubmit } from 'helpers/formHelpers'
import { connectShadedLoadable } from 'helpers/loadableHelpers'
import { homesGuard } from 'guards/propertyGuards'
import { toggleHomeEditActionCreator } from 'actions/settingsActions'
import { Guards } from 'interfaces/guards'
import { killFloatingPanesActionCreator } from 'actions/floatingPanesActions'
import { FloatingPaneHeader } from 'components/features/pfm/FloatingPaneHeader/FloatingPaneHeader'


const TYPED_PANE = {
  type: 'house-edit' as const,
}

type HouseEditPaneProps = {

}

const HouseEditPaneComponent: FC<HouseEditPaneProps> = (props) => {
  const {} = props

  const paneSelector = useMemo(createFloatingPaneByTypeSelector, [])
  const pane = useStoreSelector((state) => paneSelector(state, TYPED_PANE)) as Extract<FloatingPane, typeof TYPED_PANE>

  const { homeId = null } = pane || {}

  return (
    <HouseEditPaneInner homeId={ homeId } key={ `${ homeId }` } />
  )
}

type HouseEditPaneInnerProps = {
  homeId: number | null
}
const HouseEditPaneInner: FC<HouseEditPaneInnerProps> = (props) => {
  const { homeId } = props

  const dispatch = useStoreDispatch()
  useEffect(() => {
    return () => {
      dispatch(toggleHomeEditActionCreator(null))
    }
  }, [dispatch])

  const handleClose = useCallback(() => {
    dispatch(killFloatingPanesActionCreator('house-edit'))
  }, [dispatch])

  const editingHome = useStoreSelector((state) => {
    const { homes } = state.data.property
    if (!homes || !homeId) {
      return null
    }
    return getItemFromCollection(homes, homeId)
  })

  const initialValues = useMemo(() => {
    return editingHome ? homeToFormValues(editingHome) : HOME_FORM_INITIAL_VALUES
  }, [editingHome])

  const headerChild = useMemo(() => (
    <FloatingPaneHeader
      title={ !!homeId ? 'Edit Your Home' : 'Add a Home' }
      compact
    />
  ), [homeId])

  return (
    <FloatingPaneLayout
      type='house-edit'
      header={ headerChild }
      onClose={ handleClose }
      cardLayout
    >
      <HouseEditForm
        homeId={ homeId || -1 }
        onCancel={ handleClose }
        onDelete={ handleClose }
        onUnlink={ handleClose }
        onSubmit={ onFormSubmit }
        initialValues={ initialValues }
      />
    </FloatingPaneLayout>
  )
}

const guards: Guards = [
  homesGuard,
]

export const HouseEditPane = connectShadedLoadable<{}, {}, {}>(() => () => ({}), undefined, guards)(HouseEditPaneComponent)
